import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import '../mortgage-remainders.css';

type Props = {
  mortgageId: string;
  userId: string;
};

type RemainderStatus = 'pending' | 'partially_settled' | 'settled' | 'cancelled';

type Remainder = {
  id: string;
  period_month: string;
  bank_charge: number;
  reimbursed_to_creditor: number;
  remainder_amount: number;
  settled_amount: number;
  settled_on: string | null;
  status: RemainderStatus;
  notes: string | null;
};

type PeriodForm = {
  periodMonth: string;
  bankCharge: string;
  reimbursed: string;
  notes: string;
};

const currency = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  maximumFractionDigits: 2,
});

const monthFormatter = new Intl.DateTimeFormat('es-MX', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function monthLabel(value: string) {
  return monthFormatter.format(new Date(`${value.slice(0, 7)}-01T00:00:00Z`));
}

function statusLabel(status: RemainderStatus) {
  if (status === 'settled') return 'Liquidado';
  if (status === 'partially_settled') return 'Pago parcial';
  if (status === 'cancelled') return 'Cancelado';
  return 'Pendiente';
}

function outstanding(row: Remainder) {
  return Math.max(0, Number(row.remainder_amount) - Number(row.settled_amount));
}

export default function MortgageRemainders({ mortgageId, userId }: Props) {
  const [rows, setRows] = useState<Remainder[]>([]);
  const [form, setForm] = useState<PeriodForm>({
    periodMonth: currentMonth(),
    bankCharge: '',
    reimbursed: '0',
    notes: '',
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [settlingId, setSettlingId] = useState<string | null>(null);
  const [settlementAmount, setSettlementAmount] = useState('');
  const [settlementOn, setSettlementOn] = useState(today());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function loadRows(clearMessage = false) {
    setLoading(true);
    if (clearMessage) setMessage('');

    const { data, error } = await supabase
      .from('reimbursement_remainders_public')
      .select('id, period_month, bank_charge, reimbursed_to_creditor, remainder_amount, settled_amount, settled_on, status, notes')
      .eq('mortgage_id', mortgageId)
      .neq('status', 'cancelled')
      .order('period_month', { ascending: false });

    if (error) {
      setMessage('No fue posible cargar los remanentes hipotecarios.');
      setLoading(false);
      return;
    }

    setRows((data ?? []) as Remainder[]);
    setLoading(false);
  }

  useEffect(() => {
    void loadRows(true);
  }, [mortgageId]);

  const summary = useMemo(() => {
    const active = rows.filter((row) => row.status !== 'settled' && row.status !== 'cancelled');
    const pendingTotal = active.reduce((total, row) => total + outstanding(row), 0);
    const reimbursedTotal = rows.reduce((total, row) => total + Number(row.reimbursed_to_creditor ?? 0) + Number(row.settled_amount ?? 0), 0);
    return {
      pendingTotal,
      pendingPeriods: active.length,
      reimbursedTotal,
    };
  }, [rows]);

  function resetForm() {
    setEditingId(null);
    setForm({ periodMonth: currentMonth(), bankCharge: '', reimbursed: '0', notes: '' });
  }

  function editRow(row: Remainder) {
    setEditingId(row.id);
    setForm({
      periodMonth: row.period_month.slice(0, 7),
      bankCharge: String(row.bank_charge),
      reimbursed: String(row.reimbursed_to_creditor),
      notes: row.notes ?? '',
    });
    setMessage('Editando el periodo seleccionado. Los pagos ya aplicados se conservarán.');
  }

  async function savePeriod(event: FormEvent) {
    event.preventDefault();

    const bankCharge = Number(form.bankCharge);
    const reimbursed = Number(form.reimbursed);
    if (!form.periodMonth || !Number.isFinite(bankCharge) || bankCharge < 0 || !Number.isFinite(reimbursed) || reimbursed < 0) {
      setMessage('Ingresa un periodo y cantidades válidas.');
      return;
    }

    const periodMonth = `${form.periodMonth}-01`;
    const existing = editingId
      ? rows.find((row) => row.id === editingId)
      : rows.find((row) => row.period_month === periodMonth);
    const settledAmount = Number(existing?.settled_amount ?? 0);
    const remainderAmount = Math.max(0, bankCharge - reimbursed);

    if (settledAmount > remainderAmount) {
      setMessage('El nuevo remanente no puede ser menor que los pagos parciales ya registrados.');
      return;
    }

    const nextStatus: RemainderStatus = remainderAmount <= settledAmount
      ? 'settled'
      : settledAmount > 0
        ? 'partially_settled'
        : 'pending';

    setBusy(true);
    setMessage('');

    const payload = {
      mortgage_id: mortgageId,
      period_month: periodMonth,
      bank_charge: bankCharge,
      reimbursed_to_creditor: reimbursed,
      remainder_amount: remainderAmount,
      settled_amount: settledAmount,
      settled_on: nextStatus === 'settled' ? existing?.settled_on ?? today() : null,
      status: nextStatus,
      notes: form.notes.trim() || null,
      updated_at: new Date().toISOString(),
    };

    const result = existing
      ? await supabase.from('reimbursement_remainders_public').update(payload).eq('id', existing.id)
      : await supabase.from('reimbursement_remainders_public').insert({ ...payload, created_by: userId });

    if (result.error) {
      setMessage('No fue posible guardar el remanente.');
    } else {
      setMessage(existing ? 'Periodo actualizado sin duplicar el registro.' : 'Remanente hipotecario registrado.');
      resetForm();
      await loadRows();
    }

    setBusy(false);
  }

  function beginSettlement(row: Remainder) {
    setSettlingId(row.id);
    setSettlementAmount('');
    setSettlementOn(today());
    setMessage('Registra únicamente el importe adicional entregado para liquidar este remanente.');
  }

  async function registerSettlement(event: FormEvent) {
    event.preventDefault();
    const row = rows.find((item) => item.id === settlingId);
    const amount = Number(settlementAmount);
    if (!row || !Number.isFinite(amount) || amount <= 0) {
      setMessage('Ingresa un abono válido.');
      return;
    }

    const pending = outstanding(row);
    if (amount > pending) {
      setMessage(`El abono no puede exceder el saldo pendiente de ${currency.format(pending)}.`);
      return;
    }

    const newSettled = Number(row.settled_amount) + amount;
    const isSettled = newSettled >= Number(row.remainder_amount);

    setBusy(true);
    setMessage('');
    const { error } = await supabase
      .from('reimbursement_remainders_public')
      .update({
        settled_amount: newSettled,
        settled_on: settlementOn,
        status: isSettled ? 'settled' : 'partially_settled',
        updated_at: new Date().toISOString(),
      })
      .eq('id', row.id);

    if (error) {
      setMessage('No fue posible registrar el abono al remanente.');
    } else {
      setMessage(isSettled ? 'Remanente liquidado.' : 'Pago parcial registrado.');
      setSettlingId(null);
      setSettlementAmount('');
      await loadRows();
    }
    setBusy(false);
  }

  if (loading) return <section className="remainder-state">Cargando remanentes…</section>;

  return (
    <section className="remainder-page">
      <div className="remainder-heading">
        <div>
          <p>Parte integral de la hipoteca</p>
          <h2>Remanentes con el acreedor</h2>
          <span>Registra la diferencia entre el cargo de HSBC y lo efectivamente reembolsado. Los pagos posteriores reducen el saldo sin alterar la mensualidad ni los abonos a capital.</span>
        </div>
      </div>

      <section className="remainder-summary">
        <article><span>Total por liquidar</span><strong>{currency.format(summary.pendingTotal)}</strong><small>Visible también en el portal acreedor</small></article>
        <article><span>Periodos pendientes</span><strong>{summary.pendingPeriods}</strong><small>Incluye pagos parciales</small></article>
        <article><span>Total entregado</span><strong>{currency.format(summary.reimbursedTotal)}</strong><small>Reembolsos iniciales y liquidaciones</small></article>
      </section>

      <div className="remainder-layout">
        <form className="remainder-form" onSubmit={savePeriod}>
          <div className="remainder-card-heading">
            <div><small>{editingId ? 'Corrección' : 'Nuevo periodo'}</small><h3>{editingId ? 'Modificar remanente' : 'Registrar remanente'}</h3></div>
            {editingId && <button type="button" onClick={resetForm}>Cancelar edición</button>}
          </div>
          <div className="remainder-grid">
            <label>Periodo<input type="month" value={form.periodMonth} onChange={(event) => setForm({ ...form, periodMonth: event.target.value })} required /></label>
            <label>Cargo de HSBC<input type="number" min="0" step="0.01" value={form.bankCharge} onChange={(event) => setForm({ ...form, bankCharge: event.target.value })} required /></label>
            <label>Reembolso o anticipo entregado<input type="number" min="0" step="0.01" value={form.reimbursed} onChange={(event) => setForm({ ...form, reimbursed: event.target.value })} required /></label>
            <label className="remainder-notes">Notas<textarea rows={3} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} placeholder="Ej. anticipo para la mensualidad o aclaración del periodo" /></label>
          </div>
          <div className="remainder-preview"><span>Remanente calculado</span><strong>{currency.format(Math.max(0, Number(form.bankCharge || 0) - Number(form.reimbursed || 0)))}</strong></div>
          <button className="remainder-primary" type="submit" disabled={busy}>{busy ? 'Guardando…' : editingId ? 'Guardar corrección' : 'Registrar periodo'}</button>
        </form>

        <section className="remainder-history">
          <div className="remainder-card-heading"><div><small>Seguimiento</small><h3>Periodos registrados</h3></div></div>
          <div className="remainder-list">
            {rows.map((row) => {
              const pending = outstanding(row);
              const isCurrent = row.period_month.slice(0, 7) === currentMonth();
              return (
                <article key={row.id} className={row.status === 'settled' ? 'is-settled' : ''}>
                  <div className="remainder-row-heading">
                    <div><strong>{monthLabel(row.period_month)}</strong><span>{isCurrent ? 'Mensualidad vigente' : 'Periodo histórico'}</span></div>
                    <span className={`remainder-status remainder-status--${row.status}`}>{statusLabel(row.status)}</span>
                  </div>
                  <div className="remainder-values">
                    <span>Cargo HSBC<strong>{currency.format(Number(row.bank_charge))}</strong></span>
                    <span>Reembolso inicial<strong>{currency.format(Number(row.reimbursed_to_creditor))}</strong></span>
                    <span>Pagos posteriores<strong>{currency.format(Number(row.settled_amount))}</strong></span>
                    <span>Saldo pendiente<strong>{currency.format(pending)}</strong></span>
                  </div>
                  {row.notes && <p>{row.notes}</p>}
                  <div className="remainder-actions">
                    <button type="button" onClick={() => editRow(row)}>Editar periodo</button>
                    {pending > 0 && <button className="is-primary" type="button" onClick={() => beginSettlement(row)}>Registrar abono</button>}
                  </div>

                  {settlingId === row.id && (
                    <form className="remainder-settlement" onSubmit={registerSettlement}>
                      <label>Abono adicional<input type="number" min="0.01" max={pending} step="0.01" value={settlementAmount} onChange={(event) => setSettlementAmount(event.target.value)} required /></label>
                      <label>Fecha<input type="date" value={settlementOn} onChange={(event) => setSettlementOn(event.target.value)} required /></label>
                      <div><button type="button" onClick={() => setSettlingId(null)}>Cancelar</button><button type="submit" disabled={busy}>Aplicar abono</button></div>
                    </form>
                  )}
                </article>
              );
            })}
            {!rows.length && <p className="remainder-empty">Todavía no existen periodos registrados.</p>}
          </div>
        </section>
      </div>

      {message && <p className="remainder-message" role="status">{message}</p>}
    </section>
  );
}
